import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { RolesService } from '../../services/roles.service';
import { TeamService } from '../../services/team.service';
import { Role, Team } from '../../types/team.types';
import { UserRoles } from '../../constants/userRoles';
import { toast } from 'react-hot-toast';
import UpgradePromptModal from '../common/UpgradePromptModal';

interface InviteMemberModalProps {
  // Common props
  teamId: string;
  team_id: string;
  team?: Team | null;
  onClose: () => void;

  // Optional props that may be provided by different parents
  teamName?: string;
  entityType?: string;
  isOpen?: boolean;

  // Choose one callback approach:
  // 1. Direct callback with email and roleId
  onInvite?: (email: string, roleId: number, teamId: string) => void;
  // 2. Success callback when invitation is sent
  onInviteSuccess?: () => void;

  // Optional flag to use static roles vs. fetched roles
  useStaticRoles?: boolean;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  teamId,
  team_id,
  team,
  onClose,
  teamName = '',
  entityType = 'Team',
  isOpen = true,
  onInvite,
  onInviteSuccess,
  useStaticRoles = false
}) => {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<number>(0); // Default to no selection
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);



  const fetchRoles = async () => {
    try {
      const response = await RolesService.getRoles(team_id);
      setRoles(response || []);
      // Set default role if we have roles
      if (response && response.length > 0) {
        // Find member role or use first non-admin role
        const memberRole = response.find(r => r.id === UserRoles.MEMBER);
        setRoleId(memberRole ? memberRole.id : (response[0].id !== 1 ? response[0].id : response[1]?.id || 0));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to load roles');
    }
  };
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!roleId) {
      setError('Please select a role');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (onInvite) {
        // Use the callback provided by parent - it handles the API call and refresh
        await onInvite(email, roleId, teamId);
      } else {
        // Make the service call directly
        await TeamService.inviteUser(email, roleId, teamId, team_id);
        toast.success(`Invitation sent to ${email}`);

        // Call the success callback if provided
        if (onInviteSuccess) {
          onInviteSuccess();
        }
      }

      // Reset form
      setEmail('');
      setRoleId(UserRoles.MEMBER);

      // Close modal
      onClose();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      setError(error.message || 'Failed to send invitation');
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getLicenseUsagePercentage = () => {
    if (!team) return 0;
    return ((team.current_member_count || 0) / (team.member_limit || 0)) * 100;
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <Modal
      title={`Invite User to Team`}
      onClose={onClose}
      isOpen={isOpen}
      size="md"
    >
      {team && (
        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            User License Usage
          </h4>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                <span>Current Usage</span>
                <span>{team.current_member_count} / {team.member_limit} licenses</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${getLicenseUsagePercentage() > 90 ? 'bg-red-500' :
                    getLicenseUsagePercentage() > 75 ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}
                  style={{ width: `${Math.min(getLicenseUsagePercentage(), 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-300">Available Licenses:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {(team.member_limit || 0) - (team.current_member_count || 0)}
              </span>
            </div>
          </div>
        </div>
      )}
      {getLicenseUsagePercentage() == 100 ? (
        <div className="text-red-500 text-sm p-2 mb-2 bg-red-50 rounded-md">
          You have reached the maximum number of licenses for this team. Please <span className="font-medium text-blue-600 hover:text-blue-700 cursor-pointer" onClick={() => setShowUpgradeModal(true)}>upgrade</span> to add more licenses.
        </div>
      ) :

        (
          <>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  required
                  autoFocus
                />

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    id="role"
                    value={roleId}
                    onChange={(e) => setRoleId(Number(e.target.value))}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value={0}>Select Role</option>
                    {roles.filter(role => role.id !== 1).map((role: Role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <div className="text-sm text-red-600">
                    {error}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading || !email || !roleId}
                >
                  Send Invitation
                </Button>
              </div>
            </form>
          </>
        )
      }
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          onClose();
        }}
        teamId={team_id || ''}
      />
    </Modal>
  );
};

export default InviteMemberModal;